import psl from "psl";

/**
	* Network related tools
	* Handles: fetch async/sync
	* @module tools/network
*/

export const fetchOptions:any = {
	method: "GET",
	headers: new Headers(),
	mode: "cors",
	cache: "default"
}

/**
	* Fetch response as json object
	* 
	* @param url	-	URL to request
	* 
	* @remarks
	* Returns a promise
*/
export function fetchJson(url:string) {
	return fetch(url)
	.then((res) => {
		if(!res.ok) return;
		return res.json();
	});
}

/**
	* Fetch response as string
	* 
	* @param url	-	URL to request
	* 
	* @remarks
	* Returns a promise
*/
export function fetchAsText(url:string) {
	return fetch(url)
	.then((res) => {
		if(!res.ok) return;
		return res.text();
	});
}

/**
	* Fetch response as json object synchronously
	* 
	* @param url	-	URL to request
	* 
	* @remarks
	* Returns json object
*/
export function fetchJsonSync(url:string) {
	const xmlHttp = new XMLHttpRequest();
	
	xmlHttp.open("GET", url, false);
	xmlHttp.overrideMimeType?.("application/json");
	xmlHttp.send();
	
	if(xmlHttp.status == 200 && xmlHttp.readyState == 4) {
		return JSON.parse(xmlHttp.responseText);
	}
}

/**
	* Fetch with custom options
	* 
	* @param url		-	URL to request
	* @param options	-	Request options
	* 
	* @remarks
	* Returns a promise
*/
export function fetchRequest(url:string, options:Object) {
	return fetch(new Request(url, options));
}

/**
	* Fetch response as json object with custom options
	* 
	* @param url		-	URL to request
	* @param options	-	Request options
	* 
	* @remarks
	* Returns a promise
*/
export function fetchRequestJson(url:string, options:Object) {
	return fetch(new Request(url, options))
	.then(res => {
		if (res.ok) {
		  return res.json()
		} else if(res.status === 404) {
		  return Promise.reject('404')
		} else {
		  return Promise.reject(res.status)
		}
	});
}

/**
	* Fetch request as POST with json object return
	* 
	* @param url     -   URL to request
	* @param form    -   Form data as object
	* 
	* @returns
	* Returns promise with json data
*/
export function postRequestJson(url:string, form:any, headers:any = {}) {
	return fetch(url, {
		method: 'POST',
		headers: {'Content-Type': 'application/json', ...headers},
		body: JSON.stringify(form)
	});
}

/**
	* Fetch request as POST with json object return
	* 
	* @param url     -   URL to request
	* @param form    -   Form data as object
	* 
	* @returns
	* Returns promise with json data
*/
export function postRequest(url:string, form:any, headers:any = {}) {
	return fetch(url, {
		method: 'POST',
		headers: {"Content-type": "application/x-www-form-urlencoded; charset=UTF-8", ...headers},
		body: Object.entries(form).map(([k,v])=>{return k+'='+v}).join('&')
	});
}

/**
	* Fetch request as PUT with json object return
	* 
	* @param url     -   URL to request
	* @param form    -   Form data as object
	* 
	* @returns
	* Returns promise with json data
*/
export function putRequest(url:string, form:any, headers:any = {}) {
	return fetch(url, {
		method: 'PUT',
		headers: {"Content-type": "application/json;charset=UTF-8", ...headers},
		body: JSON.stringify(form)
	});
}

/**
	* Fetch request as DELETE with json object return
	* 
	* @param url     -   URL to request
	* @param form    -   Form data as object
	* 
	* @returns
	* Returns promise with json data
*/
export function deleteRequest(url:string, form:any, headers:any = {}) {
	return fetch(url, {
		method: 'DELETE',
		headers: {"Content-type": "application/json;charset=UTF-8", ...headers},
		body: JSON.stringify(form)
	});
}

/**
	* Fetch request as POST
	* 
	* @param url     -   URL to request
	* @param body    -   Body of request
	* 
	* @returns
	* Returns promise with string data
*/
export function postRequestBody(url:string, body:string, headers:any = {}) {
	return fetch(url, {
		method: 'POST',
		headers: {"Content-type": "application/json;charset=UTF-8", ...headers},
		body: body
	});
}

/**
	* Download URI
	*
	* @param {string} uri - URL to download
	* @param {string} name - File name
*/
export function downloadURI(uri:string, name:string) {
	const link:any = document.createElement("a");
	
	link.download = name;
	link.target = "_blank";
	link.href = uri;
	
	document.body.appendChild(link);
	
	link.click();
	
	document.body.removeChild(link);
}

/**
	* Match hostname
	* 
	* @param {string} hostCheck - Hostname to check
*/
export function hostMatch(hostCheck:string, hostnames:string[]) {
	const parsed:any = psl.parse(hostCheck.replace("*", "any"));
	
	let match:boolean = false;
	
	hostnames.forEach((hostname:string) => {
		const hparsed:any = psl.parse(hostname.replace("*", "any"));
		
		if(hparsed.domain == parsed.domain) {
			match = true;
			return;
		}
	});
	
	return match;
}